/**
 * Paywall signal — how a `SUBSCRIPTION_REQUIRED` failure anywhere in the app
 * reaches the one dialog that can explain it.
 *
 * The clinic BFF answers a blocked write with TWO different error codes on
 * purpose (`entitlementMap.js`): `FORBIDDEN` means *this person* may not, and
 * `SUBSCRIPTION_REQUIRED` means *this clinic* may not right now. The second one
 * has a fix — renew — and until this module existed no client code branched on
 * it at all, so a receptionist blocked from booking got a generic red toast and
 * a dead end.
 *
 * Shaped exactly like the framework's `connectivitySignal`: a module-level
 * subscriber set, because the producer is the fetch layer (`bffClient`, not a
 * component) and the consumer is one dialog mounted in the shell. Threading a
 * React context between them would mean every call site that can fail had to be
 * inside a provider AND hold a hook — and `bffClient` is a plain module.
 *
 * The payload is the error's `extensions`, verbatim:
 *   { code, operation, clinicId, status, currentPeriodEnd, renewUrl }
 * The dialog reads `status`/`currentPeriodEnd` for its countdown and IGNORES
 * `renewUrl` — a server-supplied string must never drive client navigation.
 */

const listeners = new Set();

/**
 * Subscribe to paywall events. Returns the unsubscribe function, so an effect
 * can `return onPaywall(handler)` directly.
 */
export function onPaywall(handler) {
  if (typeof handler !== 'function') return () => {};
  listeners.add(handler);
  return () => listeners.delete(handler);
}

/**
 * Report a blocked-by-subscription failure.
 *
 * Never throws: this runs from inside `bffClient`'s error path, where a throw
 * would replace a useful, actionable server error with an opaque one from the
 * reporting mechanism itself.
 */
export function reportPaywall(extensions) {
  for (const handler of Array.from(listeners)) {
    try {
      handler(extensions || {});
    } catch (err) {
      console.error('paywall listener failed:', err);
    }
  }
}

// ── Dismissal ────────────────────────────────────────────────────────────────
// A second, separate channel, because the dialog must be able to be CLOSED by
// something other than the person clicking it.
//
// The case that needs it is the clinic switch. This module is a singleton and the
// dialog's open-state lives in it, so an admin who hits the paywall at a lapsed
// clinic and then switches to a paid one carried the dialog across with them —
// an entitled clinic showing a renewal wall for a subscription that is not its
// own (design §A13/E11). The subscription itself is already clinic-scoped on the
// server (`entitlementCache` is keyed by clinic); only this client-side echo was
// not.

const resetListeners = new Set();

/** Subscribe to "close the paywall". Returns the unsubscribe function. */
export function onPaywallReset(handler) {
  if (typeof handler !== 'function') return () => {};
  resetListeners.add(handler);
  return () => resetListeners.delete(handler);
}

/** Close any open paywall. Called on a clinic switch; never throws. */
export function resetPaywall() {
  for (const handler of Array.from(resetListeners)) {
    try {
      handler();
    } catch (err) {
      console.error('paywall reset listener failed:', err);
    }
  }
}

/** Test seam — drops every subscriber. Not used by app code. */
export function resetPaywallListeners() {
  listeners.clear();
  resetListeners.clear();
}
